import clsx from 'clsx'
import { intervalToDuration } from 'date-fns'
import { useIsMobile } from 'hooks/useIsMobile'
import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PATH } from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'
import { missionProgressFromLiveExecution, readLiveExecutionFromRobotRead } from 'utils/robotLiveExecution'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './MissionStats.module.css'

const MissionStats = ({
  robot,
  scenarioId,
  scenarioName,
  missionName,
}: {
  robot?: IRobotInfo
  scenarioId?: number
  scenarioName?: string
  missionName?: string
}) => {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(false)

  const getDuration = (seconds: number) => {
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 })

    // Format duration: show days if present, otherwise hours:minutes:seconds or minutes:seconds
    let formatted = ''
    if (duration.days && duration.days > 0) {
      formatted = `${duration.days}d:${duration.hours || 0}h:${duration.minutes || 0}m`
    } else if (duration.hours && duration.hours > 0) {
      formatted = `${duration.hours}h:${duration.minutes || 0}m:${duration.seconds || 0}s`
    } else if (duration.minutes && duration.minutes > 0) {
      formatted = `${duration.minutes}m:${duration.seconds || 0}s`
    } else {
      // Show seconds if less than a minute
      formatted = `${duration.seconds || 0}s`
    }

    return formatted
  }

  let job
  const activeJobId = robot?.robotRaw?.last_job_id
  let missionSequenceNumber = 0
  let missionsListLength = 0
  let missionOrder = ''
  job = robot?.robotRaw?.odysseys.find(j => j.id === activeJobId)
  missionSequenceNumber = robot?.robotRaw?.last_mission_sequence_number || 0
  missionsListLength = job && 'missions' in job && Array.isArray(job.missions) ? job.missions.length : 0
  missionOrder = `${missionSequenceNumber + 1}/${missionsListLength}`

  const missionStats = job?.mission_status_last?.mission_progress

  const totalMissionStats = missionStats?.overall_mission_feedback

  const totalPercents =
    totalMissionStats &&
    totalMissionStats.progress !== undefined &&
    totalMissionStats.overall !== undefined &&
    totalMissionStats.overall
      ? Math.round((totalMissionStats.progress / totalMissionStats.overall) * 100)
      : '-'
  const totalMeters =
    totalMissionStats &&
    totalMissionStats.progress !== undefined &&
    totalMissionStats.overall !== undefined &&
    totalMissionStats.overall
      ? `${Math.round(totalMissionStats.progress)}m/${Math.round(totalMissionStats.overall)}m`
      : '-/-'
  const totalTime =
    totalMissionStats?.forecast !== undefined &&
    totalMissionStats?.forecast > 0 &&
    totalMissionStats &&
    totalMissionStats.progress !== undefined
      ? getDuration(totalMissionStats.forecast)
      : '-'

  const totalOdysseyStats = job?.mission_status_last?.odyssey_progress?.overall_mission_feedback
  const totalOdysseyPercents =
    totalOdysseyStats &&
    totalOdysseyStats.progress !== undefined &&
    totalOdysseyStats.overall !== undefined &&
    totalOdysseyStats.overall
      ? Math.round((totalOdysseyStats.progress / totalOdysseyStats.overall) * 100)
      : '-'
  const totalOdysseyMeters =
    totalOdysseyStats &&
    totalOdysseyStats.progress !== undefined &&
    totalOdysseyStats.overall !== undefined &&
    totalOdysseyStats.overall
      ? `${Math.round(totalOdysseyStats.progress)}m/${Math.round(totalOdysseyStats.overall)}m`
      : '-/-'
  const totalOdysseyTime =
    totalOdysseyStats?.forecast !== undefined &&
    totalOdysseyStats?.forecast > 0 &&
    totalOdysseyStats &&
    totalOdysseyStats.progress !== undefined
      ? getDuration(totalOdysseyStats.forecast)
      : '-'

  const zoneStats = missionStats?.coverage_task_feedback
  const zoneMaxPossible = zoneStats && zoneStats.forecast ? `${Math.round(zoneStats.forecast)}%` : '-'
  const zoneProgress = zoneStats && zoneStats.progress ? `${Math.round(zoneStats.progress)}%` : '-'
  const zoneArea = zoneStats && zoneStats.overall ? `${Math.round(zoneStats.overall)}m\u00B2` : '-'

  const routeStats = missionStats?.moving_task_feedback
  const routeMetersTraveled = routeStats && routeStats.progress ? `${Math.round(routeStats.progress)}m` : '-'
  const routeTimeLeft =
    routeStats?.forecast !== undefined && routeStats?.forecast > 0 && routeStats && routeStats.overall
      ? getDuration(routeStats.forecast)
      : '-'
  const routeMetersTotal = routeStats && routeStats.overall ? `${Math.round(routeStats.overall)}m` : '-'

  const isGausium = robot?.vendor === VENDOR.GAUSIUM
  const gsLive = isGausium && robot?.robotRaw ? readLiveExecutionFromRobotRead(robot.robotRaw as any) : undefined
  const gsMissionProgress = missionProgressFromLiveExecution(gsLive) as Record<string, any>
  const gsState = String(gsMissionProgress?.state || '')
    .trim()
    .toUpperCase()
  const gsTaskName = String(gsMissionProgress?.name || robot?.activeTask?.missionName || '').trim()

  if (isGausium && robot) {
    const gsFeedback = gsMissionProgress?.overall_mission_feedback || {}
    const isExecuting = (gsState === 'RUNNING' || gsState === 'PAUSED') && gsTaskName.length > 0
    const gsMap =
      String(gsMissionProgress?.map_name || '').trim() || String(gsMissionProgress?.map_id || '').trim() || '—'
    const gsTask = isExecuting ? gsTaskName : '—'
    const gsProg = gsFeedback?.progress
    const gsTime = gsFeedback?.forecast
    return (
      <div className={style.root}>
        <div className={style.additionalInfoHeader}>
          <div className={style.statsTable}>
            <span className={style.statLabel}>Map:</span>
            <span className={clsx(style.statValue, style.span3)}>{gsMap}</span>

            <span className={style.statLabel}>Executing task:</span>
            <span className={clsx(style.statValue, style.span3)}>{gsTask}</span>

            <span className={style.statLabel}>Progress:</span>
            <span className={style.statValue}>{isExecuting && gsProg != null ? `${gsProg}%` : '—'}</span>

            <span className={style.statLabel}>Time:</span>
            <span className={style.statValue}>{isExecuting && gsTime != null ? String(Math.round(gsTime)) : '—'}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={style.root}>
      <div className={style.additionalInfoHeader}>
        <div className={style.statsTable}>
          <span className={style.statLabel}>Task:</span>
          <span className={clsx(style.statValue, style.span3)}>{job?.id || robot?.activeTask?.id || 0}</span>

          {((job as any)?.scenario || scenarioId) && (
            <>
              <span className={style.statLabel}>Scenario:</span>
              <span className={clsx(style.statValue, style.span3)}>
                <Link
                  to={`${PATH.SCENARIOS}${scenarioId || (job as any).scenario}`}
                  style={{ textDecoration: 'none', color: 'var(--tab-selected-color)' }}
                >
                  {scenarioName || scenarioId || (job as any).scenario}
                </Link>
              </span>
            </>
          )}

          <span className={style.statLabel}>Mission:</span>
          <span className={clsx(style.statValue, style.span3)}>
            <Link
              className={style.statValue}
              to={`${PATH.MISSION}${robot?.activeTask?.missionId || 0}`}
              target="_blank"
              style={{ textDecoration: 'none', color: 'var(--tab-selected-color)' }}
            >
              {!scenarioName && missionName ? missionName : robot?.activeTask?.missionId || 0}
              {missionsListLength > 1 && ` (${missionOrder})`}
            </Link>
          </span>

          {missionsListLength > 1 && (
            <>
              <span className={style.statLabel}>Overall progress:</span>
              <span className={style.statValue}>
                {totalOdysseyPercents}% ({totalOdysseyMeters})
              </span>
              <span className={style.statLabel}>Time:</span>
              <span className={style.statValue}>{totalOdysseyTime}</span>
            </>
          )}

          <span className={style.statLabel}>Mission progress:</span>
          <span className={style.statValue}>
            {totalPercents}% ({totalMeters})
          </span>
          <span className={style.statLabel}>Time:</span>
          <span className={style.statValue}>{totalTime}</span>
        </div>
        {!isMobile && (
          <div className={style.details} onClick={() => setIsOpen(!isOpen)}>
            <span></span>
            <ArrowSvg className={`${style.icon} ${isOpen ? style.rotate : ''}`} />
          </div>
        )}
      </div>
      {isOpen && !isMobile && (
        <div className={style.twoColumns}>
          <div className={style.column}>
            <div className={style.subtitle}>Current zone</div>
            <div>
              Max possible coverage: <span className={style.bold}>{zoneMaxPossible}</span>
            </div>
            <div>
              Zone cleaned: <span className={style.bold}>{zoneProgress}</span>
            </div>
            <div>
              Zone area: <span className={style.bold}>{zoneArea}</span>
            </div>
          </div>
          <div className={style.column}>
            <div className={style.subtitle}>Current route</div>
            <div>
              Distance: <span className={style.bold}>{routeMetersTotal}</span>
            </div>
            <div>
              Distance traveled: <span className={style.bold}>{routeMetersTraveled}</span>
            </div>
            <div>
              Time left: <span className={style.bold}>{routeTimeLeft}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MissionStats
